import type { Address, UpdateAddressCommand } from "@/domain/model/Address";
import type { AddressService } from "@/domain/port/AddressService";
import type { PageQuery } from "@/domain/port/PageQuery";
import type { HttpClient } from "@/infrastructure/HttpClient";
import type { AddressDto } from "./dto/AddressDto";
import {
  addressFromDto,
  updateAddressCommandToDto,
} from "./mappers/AddressMapper";
import type { AddressId } from "@/domain/model/Ids";

const BATCH_LIMIT = 100;

export class AddressServiceImpl implements AddressService {
  constructor(private readonly http: HttpClient) {}

  async list(q: PageQuery): Promise<{ items: Address[]; total: number }> {
    const params = toQueryParams(q);

    const { items, total } = await this.http.request<{
      items: AddressDto[];
      total: number;
    }>(`/api/addresses?${params.toString()}`);

    return { items: items.map(addressFromDto), total };
  }

  async getManyByIds(ids: AddressId[]): Promise<Address[]> {
    if (ids.length === 0) return [];

    // chunk to respect server max
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += BATCH_LIMIT) {
      chunks.push(ids.slice(i, i + BATCH_LIMIT));
    }

    const results = await Promise.all(
      chunks.map(async (chunk) => {
        const qs = new URLSearchParams({ ids: chunk.join(",") }); // CSV as per your route
        const dtos = await this.http.request<AddressDto[]>(
          `/api/addresses/_batch?${qs.toString()}`,
        );
        return dtos.map(addressFromDto);
      }),
    );

    // flatten and (optionally) preserve caller order by id
    const flat = results.flat();
    const byId = new Map(flat.map((a) => [a.id, a]));
    return ids.map((id) => byId.get(id)).filter((a): a is Address => !!a);
  }

  async getById(id: AddressId): Promise<Address> {
    const dto = await this.http.request<AddressDto>(
      `/api/addresses/${encodeURIComponent(id)}`,
    );
    return addressFromDto(dto);
  }

  async update(id: AddressId, cmd: UpdateAddressCommand): Promise<Address> {
    const request = updateAddressCommandToDto(cmd);
    const dto = await this.http.request<AddressDto>(
      `/api/addresses/${encodeURIComponent(id)}`,
      {
        method: "PUT",
        body: JSON.stringify(request),
      },
    );
    return addressFromDto(dto);
  }

  async delete(id: AddressId): Promise<Address> {
    const dto = await this.http.request<AddressDto>(
      `/api/addresses/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
    return addressFromDto(dto);
  }
}

function flattenAddressFilter(filter: any): Record<string, string> {
  const q = typeof filter?.q === "string" ? filter.q.trim() : "";
  return q ? { q } : {};
}

function toQueryParams(q: PageQuery): URLSearchParams {
  const params = new URLSearchParams({
    page: String(q.page),
    pageSize: String(q.pageSize),
    sortBy: q.sortBy ?? "updatedAt",
    sortDir: q.sortDir ?? "desc",
  });

  const flat = flattenAddressFilter(q.filter);
  for (const [k, v] of Object.entries(flat)) params.set(k, v);

  return params;
}
